/** Sin tope por defecto; pasar un número finito en `options.maxEvents` si hace falta acotar. */
export const LIVE_FEED_MAX_STORED_EVENTS = null;

/**
 * @param {object|null|undefined} response
 * @returns {{ nextPollMs: number|null, hasMore: boolean, lastEventId: string|null, matchStatus: string|null }}
 */
export function parseLiveFeedResponseMeta(response) {
  const source = response?.data ?? response ?? {};
  const nextPollMsRaw = source?.nextPollMs;
  const nextPollMs =
    nextPollMsRaw != null && Number.isFinite(Number(nextPollMsRaw))
      ? Number(nextPollMsRaw)
      : null;

  return {
    nextPollMs,
    hasMore: Boolean(source?.hasMore),
    lastEventId:
      source?.lastEventId != null && String(source.lastEventId).trim() !== ''
        ? String(source.lastEventId).trim()
        : null,
    matchStatus:
      source?.matchStatus != null && String(source.matchStatus).trim() !== ''
        ? String(source.matchStatus).trim()
        : null,
  };
}

/**
 * @param {{ hasMore?: boolean, nextPollMs?: number|null }} feedMeta
 * @param {number|null|undefined} configIntervalMs
 * @returns {number|null} ms hasta el siguiente poll; `null` = no programar.
 */
export function resolveLiveFeedPollDelayMs(feedMeta, configIntervalMs) {
  if (feedMeta?.hasMore && feedMeta.nextPollMs === 0) {
    return 0;
  }

  if (
    feedMeta?.nextPollMs != null &&
    Number.isFinite(feedMeta.nextPollMs) &&
    feedMeta.nextPollMs >= 0
  ) {
    return feedMeta.nextPollMs;
  }

  if (
    configIntervalMs != null &&
    Number.isFinite(configIntervalMs) &&
    configIntervalMs > 0
  ) {
    return configIntervalMs;
  }

  return null;
}

/**
 * @param {Array<{ hasMore?: boolean, nextPollMs?: number|null }>} metas
 * @param {number|null|undefined} configIntervalMs
 */
export function resolveCombinedPollDelayMs(metas, configIntervalMs) {
  const delays = metas
    .map((meta) => resolveLiveFeedPollDelayMs(meta, configIntervalMs))
    .filter((ms) => ms != null && Number.isFinite(ms));

  if (delays.length === 0) {
    return configIntervalMs != null &&
      Number.isFinite(configIntervalMs) &&
      configIntervalMs > 0
      ? configIntervalMs
      : null;
  }
  return Math.min(...delays);
}

/**
 * @param {object} streamEvent
 * @returns {string|null}
 */
/** Eventos que redefinen plantilla en cancha/tabla; se reaplican en cada poll aunque ya estén vistos. */
export const LINEUP_STRUCTURAL_STREAM_EVENT_TYPES = new Set(['lineup.confirmed']);

/**
 * Separa `lineup.confirmed` (siempre reaplicar) del resto (dedupe incremental).
 * @param {object[]} streamEvents
 * @returns {{ lineupConfirmed: object[], detail: object[] }}
 */
export function partitionLineupsStreamEvents(streamEvents) {
  const lineupConfirmed = [];
  const detail = [];

  if (!Array.isArray(streamEvents)) {
    return { lineupConfirmed, detail };
  }

  streamEvents.forEach((event) => {
    if (LINEUP_STRUCTURAL_STREAM_EVENT_TYPES.has(event?.eventType)) {
      lineupConfirmed.push(event);
    } else {
      detail.push(event);
    }
  });

  return { lineupConfirmed, detail };
}

export function getStreamEventDedupeKey(streamEvent) {
  if (!streamEvent || typeof streamEvent !== 'object') return null;

  const streamEventId =
    streamEvent.streamEventId != null
      ? String(streamEvent.streamEventId).trim()
      : '';
  if (streamEventId) return `stream:${streamEventId}`;

  const eventId =
    streamEvent.eventId != null ? String(streamEvent.eventId).trim() : '';
  if (eventId) return `event:${eventId}`;

  const actionId =
    streamEvent.payload?.actionId != null
      ? String(streamEvent.payload.actionId).trim()
      : '';
  if (actionId) return `action:${actionId}`;

  return null;
}

/**
 * @param {object} liveEvent — ítem mapeado de `live-match-actions`.
 * @returns {string|null}
 */
/**
 * Clave de dedupe estricta para `live-match-actions` (historias: sin duplicados por acción).
 * @param {object} liveEvent
 * @returns {string|null}
 */
export function getLiveEventDedupeKey(liveEvent) {
  if (!liveEvent || typeof liveEvent !== 'object') return null;

  const eventId =
    liveEvent.eventId != null ? String(liveEvent.eventId).trim() : '';
  if (eventId) return `ev:${eventId}`;

  const type = liveEvent.type != null ? String(liveEvent.type) : '';
  const minute = liveEvent.minute != null ? String(liveEvent.minute) : '';
  const second = liveEvent.second != null ? String(liveEvent.second) : '';
  const teamId = liveEvent.teamId != null ? String(liveEvent.teamId) : '';
  const name = liveEvent.name != null ? String(liveEvent.name) : '';

  return `fallback:${type}|${minute}|${second}|${teamId}|${name}`;
}

/**
 * Clave de merge/dedupe por `eventId` (repoll del mismo suceso).
 * @param {object} liveEvent
 * @returns {string|null}
 */
export function getLiveEventEventIdKey(liveEvent) {
  if (!liveEvent || typeof liveEvent !== 'object') return null;
  const eventId =
    liveEvent.eventId != null ? String(liveEvent.eventId).trim() : '';
  return eventId ? `ev:${eventId}` : null;
}

/**
 * Elimina duplicados por `eventId` (mantiene la primera ocurrencia).
 * @param {object[]} events
 */
export function dedupeLiveEventsByEventId(events) {
  if (!Array.isArray(events)) return [];

  const seen = new Set();
  const result = [];
  events.forEach((ev) => {
    const id = ev?.eventId != null ? String(ev.eventId).trim() : '';
    if (id) {
      if (seen.has(id)) return;
      seen.add(id);
    }
    result.push(ev);
  });
  return result;
}

/**
 * Orden ascendente por minuto y segundo (como `matchDetailsMock.json`).
 * @param {object[]} events
 */
export function sortLiveEventsAsc(events) {
  if (!Array.isArray(events) || events.length < 2) {
    return Array.isArray(events) ? events : [];
  }

  return events
    .map((e, idx) => ({ e, idx }))
    .sort((a, b) => {
      const ma =
        a.e?.minute != null && a.e.minute !== ''
          ? Number(a.e.minute)
          : Number.POSITIVE_INFINITY;
      const mb =
        b.e?.minute != null && b.e.minute !== ''
          ? Number(b.e.minute)
          : Number.POSITIVE_INFINITY;
      if (ma !== mb) return ma - mb;

      const sa = a.e?.second != null && a.e.second !== '' ? Number(a.e.second) : 0;
      const sb = b.e?.second != null && b.e.second !== '' ? Number(b.e.second) : 0;
      if (sa !== sb) return sa - sb;

      return a.idx - b.idx;
    })
    .map((x) => x.e);
}

/**
 * @param {object[]} previous
 * @param {object[]} incoming
 * @param {(item: object) => string|null} getKey
 * @param {number} [maxItems]
 */
export function mergeUniqueByKey(previous, incoming, getKey, maxItems = LIVE_FEED_MAX_STORED_EVENTS) {
  const seen = new Set();
  const merged = [];

  const push = (item) => {
    if (!item) return;
    const key = getKey(item);
    if (key) {
      if (seen.has(key)) return;
      seen.add(key);
    }
    merged.push(item);
  };

  (previous ?? []).forEach(push);
  (incoming ?? []).forEach(push);

  if (
    maxItems == null ||
    !Number.isFinite(maxItems) ||
    maxItems <= 0 ||
    merged.length <= maxItems
  ) {
    return merged;
  }
  return merged.slice(merged.length - maxItems);
}

/**
 * Eventos de stream aún no presentes en `seenKeys` (no muta el set).
 * @param {object[]} streamEvents
 * @param {Set<string>} seenKeys
 */
export function peekUnseenStreamEvents(streamEvents, seenKeys) {
  if (!Array.isArray(streamEvents) || streamEvents.length === 0) return [];

  const batchSeen = new Set();

  return streamEvents.filter((event) => {
    const key = getStreamEventDedupeKey(event);
    if (!key) return true;
    if (seenKeys.has(key) || batchSeen.has(key)) return false;
    batchSeen.add(key);
    return true;
  });
}

/**
 * Marca como vistos los eventos aplicados con éxito al estado del partido.
 * @param {object[]} streamEvents
 * @param {Set<string>} seenKeys
 */
export function commitSeenStreamEvents(streamEvents, seenKeys) {
  if (!Array.isArray(streamEvents) || streamEvents.length === 0) return;

  streamEvents.forEach((event) => {
    const key = getStreamEventDedupeKey(event);
    if (key) seenKeys.add(key);
  });
}

/**
 * Conserva solo eventos de stream aún no vistos (mutates `seenKeys`).
 * @param {object[]} streamEvents
 * @param {Set<string>} seenKeys
 */
export function filterUnseenStreamEvents(streamEvents, seenKeys) {
  const unseen = peekUnseenStreamEvents(streamEvents, seenKeys);
  commitSeenStreamEvents(unseen, seenKeys);
  return unseen;
}

/**
 * Acumula batches de `live-events` sin duplicar por {@link getStreamEventDedupeKey}.
 * @param {object[]} previous
 * @param {object[]} incoming
 * @returns {object[]}
 */
export function mergeStreamEventsAccumulator(previous, incoming) {
  const seen = new Set();
  const merged = [];

  const push = (event) => {
    if (!event || typeof event !== 'object') return;
    const key = getStreamEventDedupeKey(event);
    if (key) {
      if (seen.has(key)) return;
      seen.add(key);
    }
    merged.push(event);
  };

  (previous ?? []).forEach(push);
  (incoming ?? []).forEach(push);
  return merged;
}
